import React, { useState } from 'react';
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import PlaidLinkButton from './PlaidLinkButton';

const ImportSection = ({ 
  isEditor, 
  projectId,
  selectedAccount, 
  setSelectedAccount, 
  accounts, 
  dragging, 
  handleDrag, 
  handleDrop, 
  file, 
  setFile, 
  onImport, 
  loading,
  fetchTransactions,
  fetchAccounts
}) => {
  const [syncing, setSyncing] = useState(false);

  if (!isEditor) return null;

  const selectedAccountData = accounts.find(a => a._id === selectedAccount);
  const isPlaidConnected = selectedAccountData?.plaidConnectionId;

  const handleSync = async () => {
    if (!selectedAccount) return;
    setSyncing(true);
    try {
        const res = await axios.post(`/api/projects/${projectId}/plaid/sync/${selectedAccount}`);
        alert(`Sync successful! Added ${res.data.addedCount} new transactions.`);
        if (fetchTransactions) fetchTransactions();
        if (fetchAccounts) fetchAccounts();
    } catch (err) {
        console.error('Sync error:', err);
        alert('Failed to sync with bank. ' + (err.response?.data?.msg || ''));
    } finally {
        setSyncing(false);
    }
  };

  return (
    <div className="card shadow-sm mb-4 border-0">
      <div className="card-body bg-light rounded p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title small text-uppercase font-weight-bold text-primary mb-0">
                <i className="fas fa-file-import mr-2"></i> Import or Sync Transactions
            </h5>
            {selectedAccount && !isPlaidConnected && (
                <PlaidLinkButton 
                    projectId={projectId} 
                    accountId={selectedAccount} 
                    onUpdate={fetchAccounts} 
                />
            )}
            {selectedAccount && isPlaidConnected && (
                <Button 
                    variant="success" 
                    size="sm" 
                    onClick={handleSync} 
                    disabled={syncing}
                    className="px-3"
                >
                    {syncing ? <><Spinner size="sm" animation="border" className="mr-2" /> Syncing...</> : <><i className="fas fa-sync-alt mr-2"></i> Sync with Bank</>}
                </Button>
            )}
        </div>
        <Row className="align-items-center">
          <Col md={4}>
            <Form.Group className="mb-0">
              <Form.Label className="small font-weight-bold">Target Account</Form.Label>
              {accounts.filter(acc => ['Bank', 'Credit Card'].includes(acc.type)).length > 0 ? (
                <Form.Control as="select" size="sm" className="border-primary" value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)}>
                  <option value="">-- Select --</option>
                  {accounts.filter(acc => ['Bank', 'Credit Card'].includes(acc.type)).map(acc => (
                    <option key={acc._id} value={acc._id}>
                        {acc.name} ({acc.type}) {acc.plaidConnectionId ? ' ✓' : ''}
                    </option>
                  ))}
                </Form.Control>
              ) : (
                <div className="text-danger small">No bank accounts found. Create one in Accounts first.</div>
              )}
            </Form.Group>
          </Col>
          <Col md={8}>
            <div 
              className={`border rounded p-3 text-center transition-all ${dragging ? 'bg-primary text-white border-primary' : 'bg-white border-primary'}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              style={{ cursor: 'pointer', borderStyle: 'dashed', borderWidth: '2px' }}
              onClick={() => document.getElementById('file-upload').click()}
            >
              <i className={`fas fa-cloud-upload-alt fa-lg mb-2 ${dragging ? 'text-white' : 'text-primary'}`}></i>
              <p className="small mb-0 font-weight-bold">
                {file ? <strong>{file.name}</strong> : "Drag & Drop statement or Click to Upload"}
              </p>
              <input 
                type="file" 
                id="file-upload" 
                className="d-none" 
                onChange={e => setFile(e.target.files[0])} 
                accept=".csv,.pdf" 
              />
            </div>
          </Col>
        </Row>
        {file && (
          <div className="text-right mt-3">
            <Button variant="primary" size="sm" onClick={onImport} disabled={loading} className="px-4 shadow-sm">
              {loading ? 'Processing...' : `Import ${file.name}`}
            </Button>
            <Button variant="link" size="sm" className="text-muted ml-2" onClick={() => setFile(null)}>Cancel</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportSection;
